import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import { useStyleStore, getSpecificThemeValue, resolveColor } from "@vcl";

// Lazy require to break circular dependency:
// shared-base-option → dynamic-component-store → configuration-store
// → (any render-engine class) → shared-base-option
function useDynamicComponentStore() {
  return require("%/dynamic-component-editor/stores/dynamic-component-store").default();
}

export default class SharedBaseOption {
  stateValues = {};
  hideFromInspector = false;
  useInspectorLabel = true;
  displayOverrideIcon = undefined;
  overrideLabel = "";
  optionKey = "";
  isBundled = false;
  orientation = "vertical";
  _shouldHideOptionTool = undefined;

  static hideOptionTool = false;

  static useSubtitle = false;

  static bundleOptions = []; // only applicable when using a inspectorToolGroup

  static canSetAsVariable = true;

  // Opt-in: the dataType an option can be Context-Bound to, or null for "not
  // bindable". Off by default so binding is added surface by surface — an
  // option whose value is a composite (padding, box-shadow) needs its own
  // design before it can carry a scalar binding.
  static bindableDataType = null;

  static useDivider = true;

  static validations = [];

  static action = {};

  static descriptionMessage = "";

  static DataType = {
    Boolean: "boolean",
  };

  static $states = {
    DEFAULT: "default",
    HOVER: "hover",
  };

  static $valueOptions = {
    NUMBER: "Number",
    STRING: "String",
  };

  // Cached per subclass via a class-own property check, so subclasses do not
  // inherit a parent class's cached value. Reads from this getter avoid the
  // `new optionClass()` allocation that callers used to need just to read
  // an option's key off an instance.
  static get optionKey() {
    if (!Object.prototype.hasOwnProperty.call(this, "_optionKey")) {
      this._optionKey = this.name.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    }
    return this._optionKey;
  }

  constructor(hideFromInspector = false, useInspectorLabel = true) {
    this.optionKey = this.constructor.optionKey;
    this.hideFromInspector = hideFromInspector;
    this.useInspectorLabel = useInspectorLabel;
    if (!Object.prototype.hasOwnProperty.call(this.constructor, "userInterfaceComponent")) {
      throw new DynamicComponentError("configurationComponent is not defined in component", this.constructor);
    }

    let userInterfaceComponent = this.constructor.userInterfaceComponent;
    SharedBaseOption.assertValidInterfaceComponent(userInterfaceComponent);
  }

  static formatShadowValue(value) {
    if (value === null || value === undefined) {
      return 0;
    }

    if (typeof value === "string" && value.trim().length === 0) {
      return 0;
    }

    return value;
  }

  /* eslint-disable-next-line no-unused-vars*/
  static convertConfigValueToCompiledValue(configValue, themeId = null, themeMode = null, optionsValues = {}) {
    return configValue;
  }

  // eslint-disable-next-line no-unused-vars
  static getValue(value, themeId, themeMode = "inherit", optionsValues = {}) {
    return this.convertConfigValueToCompiledValue(value, themeId, themeMode, optionsValues);
  }

  // eslint-disable-next-line no-unused-vars
  static getModifiedRawValue(value, optionsValues = {}, state) {
    return value;
  }

  static getLogValue(value) {
    return this.convertValueToStringForTransactionLog(value);
  }

  static convertValueToStringForTransactionLog(value) {
    return value;
  }

  getConfigurationComponentConfig(optionValues, themeId, state) {
    if (typeof this.constructor.userInterfaceComponent === "function") {
      return this.constructor.userInterfaceComponent(optionValues, themeId, state);
    }
    return this.constructor.userInterfaceComponent;
  }

  getConfigurationComponent(optionValues, themeId, state) {
    if (typeof this.constructor.userInterfaceComponent === "function") {
      return this.constructor.userInterfaceComponent(optionValues, themeId, state).component;
    }
    return this.constructor.userInterfaceComponent.component;
  }

  getConfigurationComponentProps(optionValues, themeId, state) {
    if (typeof this.constructor.userInterfaceComponent === "function") {
      return this.constructor.userInterfaceComponent(optionValues, themeId, state).props || {};
    }
    return this.constructor.userInterfaceComponent.props || {};
  }

  static _getStyleValuePropertiesFromTheme = (styleVarName, themeId = null, themeMode = null, valueKey) => {
    if (themeId === null || themeId === "inherit") {
      themeId = useDynamicComponentStore().themeId;
    }
    if (themeMode === null) {
      themeMode = useDynamicComponentStore().themeMode;
    }

    if (!useStyleStore().themes[themeId]) {
      throw new DynamicComponentError(
        "Invalid theme id passed to _getStyleValuePropertiesFromTheme",
        {
          themeId,
        },
      );
    }

    return getSpecificThemeValue(styleVarName, themeId, themeMode, valueKey);
  };

  static getColorValue(option, theme, themePalette, themeId, themeMode) {
    return resolveColor(option, { themeId, themeMode });
  }

  static getFontValue(option, themeId) {
    return SharedBaseOption._getStyleValuePropertiesFromTheme(
      "font.typeface",
      themeId,
    )[option];
  }

  // VALIDATIONS
  static isRequired = value => {
    if (value === "" || value === null || value === undefined) {
      return "This field is required";
    }
  };

  static assertValidInterfaceComponent(userInterfaceComponent) {
    if (typeof userInterfaceComponent === "function") {
      try {
        userInterfaceComponent = userInterfaceComponent();
      } catch {
        // Function-form userInterfaceComponent may depend on Pinia/stores that aren't
        // ready yet at construction time. Skip validation in that case.
        return;
      }
    }

    import(`%/dynamic-component-editor/inspector/tools/${userInterfaceComponent.component}.vue`).then(tool => {
      const propKeys = Object.keys(tool.default.props || {});
      const ignoreProps = ["modelValue", "identifier"];
      const requiredPropKeys = propKeys.filter(key => tool.default.props[key].required && !ignoreProps.includes(key));

      if (requiredPropKeys.length > 0) {
        requiredPropKeys.forEach(propKey => {
          if (!Object.prototype.hasOwnProperty.call(userInterfaceComponent.props || {}, propKey)) {
            throw new DynamicComponentError(
              `Missing validation for required prop ${propKey} in ${userInterfaceComponent.component}`,
              userInterfaceComponent.props,
            );
          }
        });
      }
    });
  }

  // GETTERS
  get label() {
    return this.overrideLabel
    || this.constructor.label
    || this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).label;
  }

  get useCustomLabel() {
    const fromConfig = this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).useCustomLabel;
    if (fromConfig !== undefined) {
      return fromConfig;
    }

    return !this.useInspectorLabel;
  }

  get showVariableLinking() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).useDefaultVariableLinking ?? true;
  }

  get useVerticalOrientation() {
    const useVertical = this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).useVerticalOrientation;
    if (useVertical !== undefined) {
      return useVertical;
    }
    return this.orientation === "vertical";
  }

  get bundleOptions() {
    return this.constructor.bundleOptions;
  }

  get shouldHideFromInspector() {
    return this.hideFromInspector;
  }

  get canSetAsVariable() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).canSetAsVariable
    ?? this.constructor.canSetAsVariable;
  }

  get useDivider() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).useDivider || this.constructor.useDivider;
  }

  set useDivider(value) {
    this.constructor.useDivider = value;
  }

  get defaultValue() {
    return this.constructor.defaultValue;
  }

  get getValue() {
    return this.constructor.getValue;
  }

  get convertValueToStringForTransactionLog() {
    return this.constructor.convertValueToStringForTransactionLog;
  }

  get convertConfigValueToCompiledValue() {
    return this.constructor.convertConfigValueToCompiledValue;
  }

  get action() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).action
    || this.constructor.action;
  }

  get validations() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).validations
    || this.constructor.validations;
  }

  get formatShadowValue() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).formatShadowValue
    || this.constructor.formatShadowValue;
  }

  get optionDescription() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).descriptionMessage ?? this.constructor.descriptionMessage;
  }

  get useSubtitle() {
    const fromConfig = this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).useSubtitle;

    if (fromConfig !== undefined) {
      return fromConfig;
    }

    return this.constructor.useSubtitle;
  }

  get shouldHideOptionTool() {
    const fromConfig = this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).hideOptionTool;

    if (fromConfig !== undefined) {
      return fromConfig;
    }

    if (this._shouldHideOptionTool !== undefined) {
      return this._shouldHideOptionTool;
    }

    return this.constructor.hideOptionTool;
  }

  get canUpdateLabel() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).canUpdateLabel;
  }

  set canUpdateLabel(value) {
    this.constructor.canUpdateLabel = value;
  }

  get optionDataType() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).optionDataType;
  }

  get getCustomLabelWhenVariableLinked() {
    return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).getCustomLabelWhenVariableLinked;
  }

  get shouldDisplayOverrideIcon() {
    if (this.displayOverrideIcon === undefined) {
      return this.getConfigurationComponentConfig({}, useDynamicComponentStore().themeId).displayOverrideIcon ?? true;
    }

    return this.displayOverrideIcon;
  }
}
